import axios from 'axios';
import i18next from 'i18next';
import { reportApiError } from './errorReporter';
import { normalizeLanguageCode } from '../utils/languagePairPolicy';
import { isSpeakable } from '../utils/speakableText';
import { isSelfPacedPaywallError } from '../shared/billing/personalLearning';

// Raised when the server refuses self-paced study for want of a plan; the
// SelfPacedPaywall dialog listens for it.
export const SELF_PACED_PAYWALL_EVENT = 'selfPacedPaywall';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000, // 15-second timeout to avoid hanging requests
});

const GET_CACHE = new Map();

const stableSerialize = (value) => {
  if (!value || typeof value !== 'object') return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
  return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`).join(',')}}`;
};

const getCacheKey = (url, config = {}) => {
  const activeProgramId = localStorage.getItem('activeLearningProgramId') || '';
  const accountId = localStorage.getItem('userId') || (localStorage.getItem('guestMode') === 'true' ? 'guest' : 'anonymous');
  return `${url}|account:${accountId}|program:${activeProgramId}|${stableSerialize(config.params || {})}`;
};

const cachedGet = (url, config = {}, ttlMs = 10000) => {
  const key = getCacheKey(url, config);
  const now = Date.now();
  const cached = GET_CACHE.get(key);
  if (cached && cached.expiresAt > now) return cached.promise;

  const promise = api.get(url, config).catch((error) => {
    GET_CACHE.delete(key);
    throw error;
  });
  GET_CACHE.set(key, { expiresAt: now + ttlMs, promise });
  return promise;
};

const invalidateCachedGets = (predicate) => {
  for (const key of GET_CACHE.keys()) {
    if (predicate(key)) GET_CACHE.delete(key);
  }
};

const DEVICE_ID_KEY = 'lingoDeviceId';

const createDeviceId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
};

const getDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = createDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

const clearSession = ({ preserveGuest = false } = {}) => {
  [
    'token',
    'refreshToken',
    'userId',
    'username',
    // Left behind by an earlier version of this list, which is how a cleared
    // session could still render a half-identified account.
    'userFullName',
    'userEmail',
    'userRole',
    'subscriptionTier',
    'aiEntitlements',
    'emailVerified',
    'needsLanguageSetup',
    'activeLearningProgramId',
  ].forEach((key) => localStorage.removeItem(key));
  if (!preserveGuest) {
    localStorage.removeItem('guestMode');
  }
};

const isCurrentUserProfileRequest = (config) => {
  const userId = localStorage.getItem('userId');
  if (!userId || !config?.url) return false;
  const url = config.url.split('?')[0];
  return (config.method || 'get').toLowerCase() === 'get' && url === `/users/${userId}`;
};

const isProtectedRequest = (url = '') => {
  const cleanUrl = url.split('?')[0];
  return (
    cleanUrl.startsWith('/users/') ||
    cleanUrl.startsWith('/progress/') ||
    cleanUrl.startsWith('/flashcards/user/') ||
    cleanUrl === '/auth/activity'
  );
};

export function applyExpectedStatusValidation(config = {}) {
  const expected = Array.isArray(config.expectedStatuses) ? config.expectedStatuses : [];
  if (!expected.length) return config;
  const existing = typeof config.validateStatus === 'function'
    ? config.validateStatus
    : (status) => status >= 200 && status < 300;
  config.validateStatus = (status) => existing(status) || expected.includes(status);
  return config;
}

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const isGuest = localStorage.getItem('guestMode') === 'true';
  let token = localStorage.getItem('token');
  const url = config.url || '';

  if (isGuest && token) {
    clearSession({ preserveGuest: true });
    token = null;
  }

  if (!token && isProtectedRequest(url)) {
    clearSession({ preserveGuest: isGuest });
    const error = new axios.Cancel('Skipped protected request without an active session.');
    error.code = 'ERR_AUTH_REQUIRED';
    throw error;
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['X-Lingo-Device-Id'] = getDeviceId();
  const activeProgramId = localStorage.getItem('activeLearningProgramId');
  if (activeProgramId && !config.skipLearningProgramHeader) {
    config.headers['X-Learning-Program-Id'] = activeProgramId;
  }
  // Active UI language — the backend localizes response `message` strings to
  // this (see backend/middleware/localizeResponses.js).
  config.headers['X-App-Language'] = config.appLanguage || i18next.language || 'en';
  return applyExpectedStatusValidation(config);
});

// Track whether a token refresh is in progress to avoid concurrent refreshes
let refreshPromise = null;

const isAuthRequest = (url = '') => (
  url.includes('/auth/refresh') ||
  url.includes('/auth/login') ||
  url.includes('/auth/register') ||
  url.includes('/auth/google')
);

// Guard so concurrent 401s from a single stale token don't each report
// and re-dispatch sessionExpired. Reset on any successful response.
let sessionExpiryActive = false;

const endExpiredSession = (error, phase) => {
  if (sessionExpiryActive) return;
  sessionExpiryActive = true;
  if (error && phase) reportApiError(error, { phase });
  clearSession();
  window.dispatchEvent(new CustomEvent('sessionExpired'));
};

// Only a definitive answer from the server means "this sign-in is over".
// Anything else — a 5xx, a rate limit, a proxy hiccup, an offline laptop — is
// temporary, and treating it as an expiry is what used to sign people out for
// no reason. When in doubt the session is kept: the worst case is one failed
// request the user can retry, versus losing their place entirely.
const isSessionEndingRefreshFailure = (refreshError) => {
  const status = refreshError?.response?.status;
  if (status === 401 || status === 403) return true;
  // 400 covers a refresh token the browser dropped or truncated — the server
  // rejected the request outright, so there is nothing to retry with.
  if (status === 400) return true;
  return false;
};

// Run the refresh exactly once per browser tab at a time, and never at all if
// another tab has already stored a newer token (tabs share localStorage, so the
// stale request just needs to retry with what is already there).
const refreshAccessToken = (previousToken) => {
  const stored = localStorage.getItem('token');
  if (previousToken && stored && stored !== previousToken) {
    // Another tab (or an earlier request in this one) already refreshed.
    return Promise.resolve(stored);
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return Promise.reject(new Error('No refresh token'));

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_URL}/auth/refresh`, { refreshToken }, { headers: { 'X-Lingo-Device-Id': getDeviceId() } })
      .then((res) => {
        const newToken = res.data.token;
        localStorage.setItem('token', newToken);
        if (res.data.refreshToken) {
          localStorage.setItem('refreshToken', res.data.refreshToken);
        }
        sessionExpiryActive = false;
        return newToken;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

export { refreshAccessToken };

const isExpectedStatus = (config, status) => {
  const expected = config?.expectedStatuses;
  return Array.isArray(expected) && status != null && expected.includes(status);
};

// Retry logic for network errors and 5xx server errors + auto-refresh on 401
api.interceptors.response.use(
  (response) => {
    sessionExpiryActive = false;
    return response;
  },
  async (error) => {
    const config = error.config;

    // Step-up auth required for a sensitive action — hold the original promise
    // open while the UI prompts for a password, then transparently retry so
    // the caller never has to know step-up happened.
    if (error.response?.status === 401 && error.response?.data?.code === 'STEP_UP_REQUIRED') {
      return new Promise((resolve, reject) => {
        window.dispatchEvent(new CustomEvent('stepUpRequired', {
          detail: {
            maxAgeMinutes: error.response.data.maxAgeMinutes,
            onComplete: () => api(config).then(resolve, reject),
            onCancel: () => reject(error),
          },
        }));
      });
    }

    // Self-paced study needs a plan. Every learning screen can meet this, so it
    // is answered once, by the paywall dialog, rather than by each screen's own
    // error state. The request still rejects so callers stop loading.
    if (isSelfPacedPaywallError(error)) {
      window.dispatchEvent(new CustomEvent(SELF_PACED_PAYWALL_EVENT));
      return Promise.reject(error);
    }

    // Auto-refresh on 401 (expired access token).
    // Rotated refresh tokens come back on every successful refresh; store both.
    if (
      error.response?.status === 401 &&
      config &&
      !config._refreshed &&
      !isAuthRequest(config.url)
    ) {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        config._refreshed = true;
        try {
          // The token this request was signed with — if it has already moved on
          // (another tab refreshed while this request was in flight), retrying
          // with the current one is enough and no refresh call is made.
          const sentToken = String(config.headers?.Authorization || '').replace('Bearer ', '');
          const newToken = await refreshAccessToken(sentToken);
          config.headers.Authorization = `Bearer ${newToken}`;
          return api(config);
        } catch (refreshError) {
          // Only end the session when the server said, definitively, that this
          // sign-in is over. A 5xx, a rate limit, or an offline laptop is
          // temporary: keep the tokens so the next attempt recovers instead of
          // dumping the learner back on the sign-in page.
          if (isSessionEndingRefreshFailure(refreshError)) {
            endExpiredSession(error, 'auth-refresh-failed');
          } else {
            reportApiError(refreshError || error, { phase: 'auth-refresh-failed' });
          }
          return Promise.reject(error);
        }
      }
      endExpiredSession(error, 'auth-expired');
    }

    // Only retry GET requests, max 2 retries
    if (
      config &&
      !config._retryCount &&
      config.method === 'get' &&
      (!error.response || error.response.status >= 500)
    ) {
      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount <= 2) {
        await new Promise((r) => setTimeout(r, config._retryCount * 500));
        return api(config);
      }
    }
    // Detect mid-session suspension
    if (
      error.response?.status === 403 &&
      error.response?.data?.message === 'Account suspended' &&
      localStorage.getItem('token')
    ) {
      clearSession();
      window.dispatchEvent(new CustomEvent('accountSuspended'));
    }
    // Detect deleted current account without logging out for ordinary missing content.
    if (
      error.response?.status === 404 &&
      isCurrentUserProfileRequest(config) &&
      localStorage.getItem('token')
    ) {
      clearSession();
      window.dispatchEvent(new CustomEvent('accountSuspended'));
    }
    if (!isExpectedStatus(config, error.response?.status)) {
      reportApiError(error);
    }
    return Promise.reject(error);
  }
);

// Guest XP helpers — stored in localStorage, transferred on signup/login
export const guestXPHelper = {
  get: () => parseInt(localStorage.getItem('guestXP') || '0', 10),
  add: (points = 1) => {
    const current = parseInt(localStorage.getItem('guestXP') || '0', 10);
    const updated = current + points;
    localStorage.setItem('guestXP', String(updated));
    window.dispatchEvent(new CustomEvent('xpUpdated', { detail: { totalXP: updated, isGuest: true } }));
    return updated;
  },
  clear: () => localStorage.removeItem('guestXP'),
};

export const authService = {
  register: (username, email, password, guestXP, nativeLanguage, targetLanguage, fullName = '') =>
    api.post('/auth/register', { username, email, password, guestXP, nativeLanguage, targetLanguage, fullName }),
  login: (email, password, guestXP) =>
    api.post('/auth/login', { email, password, guestXP }),
  googleLogin: (credential, guestXP, nativeLanguage, targetLanguage) =>
    api.post('/auth/google', { credential, guestXP, nativeLanguage, targetLanguage }),
  // Who is signed in, according to the token alone. Used at start-up to rebuild
  // the learner's identity so the app never renders a signed-in shell with a
  // blank name in it.
  session: () => api.get('/auth/session'),
  resendVerification: () =>
    api.post('/auth/resend-verification'),
  forgotPassword: (email) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) =>
    api.post('/auth/reset-password', { token, password }),
  trackActivity: (userId, timeSpent) =>
    api.post('/auth/activity', { userId, timeSpent }, { background: true }),
  // Server-side logout: invalidates the refresh chain so a stolen refresh
  // token can't be replayed after the user signs out.
  logout: () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return Promise.resolve();
    return axios.post(`${API_URL}/auth/logout`, { refreshToken }).catch(() => {});
  },
  // Sign out every remembered device. The counterpart to staying signed in for
  // months: the learner needs a way to revoke a device they no longer have.
  logoutAll: () => api.post('/auth/logout-all'),
  // Step-up: re-verify the user's password and receive a fresh `authAt` so
  // they can perform a sensitive action without a full re-login.
  stepUp: (password) =>
    api.post('/auth/step-up', { password }),
};

const getStoredLanguageCode = (key, fallback = '') => {
  const raw = localStorage.getItem(key) || '';
  const normalized = normalizeLanguageCode(raw) || fallback;
  if (raw && normalized && normalized !== raw) {
    localStorage.setItem(key, normalized);
  }
  return normalized;
};

const getLanguageParams = () => ({
  targetLang: getStoredLanguageCode('targetLanguage'),
  nativeLang: getStoredLanguageCode('nativeLanguage'),
});

export const semesterInterestService = {
  submit: (payload) =>
    api.post('/semester-interest', payload, { timeout: 30000 }),
};

export const cohortService = {
  // Public: open cohorts a learner can register for. The `_fresh` param is the
  // cache buster — deliberately NOT a `Cache-Control` request header, which
  // would force a CORS preflight (and fail it unless the API allow-lists the
  // header) for no gain: the endpoint already answers `no-store`.
  list: () => api.get('/cohorts', { params: { _fresh: Date.now() } }),
  get: (cohortId) => api.get(`/cohorts/${cohortId}`, { params: { _fresh: Date.now() } }),
  // Requires auth: the learner's own registrations, including finished ones.
  //
  // 404 is expected rather than a defect: an API build without this route sends
  // /cohorts/mine into /cohorts/:cohortId, which answers COHORT_NOT_FOUND. The
  // caller already falls back to the learning-hub contexts, and this screen polls
  // on an interval — reporting each poll turned one un-deployed route into 385
  // dashboard rows in two days.
  listMine: () => api.get('/cohorts/mine', {
    params: { _fresh: Date.now() },
    expectedStatuses: [404],
  }),
  // 409 COHORT_PROGRAM_SWITCH_REQUIRED is part of this flow, not a failure: the
  // server refuses to open cohort work while another commitment is selected,
  // and the caller asks the learner to confirm the switch first.
  start: (cohortId) => api.post(`/cohorts/${cohortId}/start`, {}, { expectedStatuses: [409] }),
  // Requires auth (the completion gate). Enrolls the signed-in user at the
  // chosen CEFR level.
  enroll: (cohortId, level, targetLanguage, options = {}) => api.post(`/cohorts/${cohortId}/enroll`, {
    level,
    targetLanguage,
    applyForScholarship: options.applyForScholarship === true,
    scholarshipStatement: options.scholarshipStatement || '',
    scholarshipTrack: options.scholarshipTrack || '',
    scholarshipEvidence: Array.isArray(options.scholarshipEvidence) ? options.scholarshipEvidence : [],
    paymentConfirmed: options.paymentConfirmed === true,
    // Returning from checkout: the server reads the level and language the
    // learner chose before paying from this reference.
    ...(options.paymentReference ? { paymentReference: options.paymentReference } : {}),
  }),
  applyForScholarship: (cohortId, statement, options = {}) => api.post(`/cohorts/${cohortId}/scholarship`, {
    statement,
    scholarshipTrack: options.scholarshipTrack || '',
    scholarshipEvidence: Array.isArray(options.scholarshipEvidence) ? options.scholarshipEvidence : [],
  }),
  getSemester: (cohortId) => api.get(`/cohorts/${cohortId}/semester`),
  // Instructor view of who is behind. 403 for anyone who does not oversee the
  // cohort, which includes every peer learner -- and every learner's semester
  // page asks once, so the 403 is the expected answer, not a fault worth
  // filing against the error dashboard.
  getRoster: (cohortId) => api.get(`/cohorts/${cohortId}/roster`, { expectedStatuses: [403] }),
  // One learner, fixed localized copy, at most once a day. 409 means somebody
  // already nudged them today, which is an answer rather than a failure.
  nudgeLearner: (cohortId, userId) =>
    api.post(`/cohorts/${cohortId}/roster/nudge`, { userId }, { expectedStatuses: [409] }),
  // Marks the cohort welcome as seen. Not guarded by the cohort-program check,
  // so it works on the very first arrival, before the learner has switched.
  acknowledgeWelcome: (cohortId) => api.post(`/cohorts/${cohortId}/semester/welcome`, {}),
  // 409 on these three is the cohort-program guard (or a closed window), which
  // the screens already surface — it is not a defect to report.
  submitFoundationDiagnostic: (cohortId, responses, writingConfirmed) => api.post(`/cohorts/${cohortId}/semester/foundation-diagnostic`, { responses, writingConfirmed }, { expectedStatuses: [409] }),
  completeSemesterActivity: (cohortId, activityId) => api.post(`/cohorts/${cohortId}/semester/activities/${encodeURIComponent(activityId)}/complete`, {}, { expectedStatuses: [409] }),
  completeReinforcement: (cohortId, reinforcementId) => api.post(`/cohorts/${cohortId}/semester/reinforcement/complete`, { reinforcementId }, { expectedStatuses: [409] }),
  proposeClassName: (cohortId, name) => api.post(`/cohorts/${cohortId}/semester/class-name-proposals`, { name }),
  voteClassName: (cohortId, proposalId, approve) => api.post(`/cohorts/${cohortId}/semester/class-name-proposals/${proposalId}/vote`, { approve }),
  issueSemesterCertificate: (cohortId, fullName) => api.post(`/cohorts/${cohortId}/semester/certificate`, { fullName }, { expectedStatuses: [400, 409] }),
  // Self-service on the learner's own registration. 409 is the expected answer
  // when the screen has gone stale (the semester started, the destination
  // filled up), which the manage panel reports in place — not a defect.
  updateRegistration: (cohortId, changes = {}) => api.patch(`/cohorts/${cohortId}/registration`, {
    ...(changes.level === undefined ? {} : { level: changes.level }),
    ...(changes.targetLanguage === undefined ? {} : { targetLanguage: changes.targetLanguage }),
  }, { expectedStatuses: [400, 409] }),
  transferRegistration: (cohortId, toCohortId, changes = {}) => api.post(`/cohorts/${cohortId}/transfer`, {
    toCohortId,
    ...(changes.level === undefined ? {} : { level: changes.level }),
    ...(changes.targetLanguage === undefined ? {} : { targetLanguage: changes.targetLanguage }),
  }, { expectedStatuses: [400, 404, 409] }),
  withdraw: (cohortId) => api.post(`/cohorts/${cohortId}/withdraw`, {}, { expectedStatuses: [404] }),
  dismissRegistration: (cohortId) => api.delete(`/cohorts/${cohortId}/registration`, { expectedStatuses: [404, 409] }),
};

export const reviewService = {
  // Public approved reviews for the landing page (lightly cached).
  listApproved: () =>
    cachedGet('/reviews/approved', {}, 60000),
  submit: (payload) =>
    api.post('/reviews', payload, { timeout: 30000 }),
};

export const contactService = {
  sendMessage: (message) =>
    api.post('/contact', message, { timeout: 30000 }),
};

export const notificationService = {
  list: (params = {}) =>
    api.get('/notifications', { params }),
  unreadCount: () =>
    api.get('/notifications/unread-count'),
  markRead: (notificationId) =>
    api.put(`/notifications/${notificationId}/read`),
  markAllRead: () =>
    api.put('/notifications/read-all'),
  archive: (notificationId) =>
    api.delete(`/notifications/${notificationId}`),
  restore: (notificationId) =>
    api.put(`/notifications/${notificationId}/restore`),
  adminBroadcast: (payload) =>
    api.post('/notifications/admin/broadcast', payload),
};

export const certificateService = {
  list: () =>
    api.get('/certificates'),
  getClassLessonStatus: (classLessonId) => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get(`/certificates/class-lessons/${classLessonId}/status`, {
      params: { targetLang, nativeLang },
    });
  },
  issueClassLessonCertificate: (classLessonId, payload = {}) => {
    const { targetLang: targetLanguage, nativeLang: nativeLanguage } = getLanguageParams();
    return api.post(`/certificates/class-lessons/${classLessonId}/issue`, {
      ...payload,
      targetLanguage,
      nativeLanguage,
    }, { expectedStatuses: [402] });
  },
  verify: (certificateId, params = {}) =>
    api.get(`/certificates/verify/${encodeURIComponent(certificateId)}`, { params, expectedStatuses: [404, 410] }),
  institutionSampleLink: (organizationId) =>
    api.post(`/certificates/institution-samples/${organizationId}/link`),
  download: (certificateId, certificateLanguage = 'en', params = {}) =>
    api.get(`/certificates/verify/${encodeURIComponent(certificateId)}/download`, {
      params: {
        certLang: normalizeLanguageCode(certificateLanguage) || 'en',
        ...params,
      },
      responseType: 'blob',
      timeout: 45000,
    }),
};

// A semester on the learner's own start date, outside any cohort.
export const personalSemesterService = {
  get: () => api.get('/personal-semesters', { params: { _fresh: Date.now() } }),
  // Same three-step handshake as cohort registration: price, checkout link,
  // then the booking once Paystack sends the learner back. 202 means the
  // payment has not settled yet and the caller checks again.
  book: (payload) => api.post('/personal-semesters', payload, { timeout: 30000, expectedStatuses: [202, 409] }),
  update: (cohortId, payload) => api.patch(`/personal-semesters/${cohortId}`, payload, { expectedStatuses: [409] }),
};

export const billingService = {
  getPlans: () =>
    api.get('/billing/plans'),
  getAccount: () =>
    api.get('/billing/me', { expectedStatuses: [401] }),
  switchSubscriptionContext: (payload) =>
    api.post('/billing/subscription-context', payload),
  createCheckoutSession: ({ planId, interval = 'monthly', successUrl, cancelUrl, discountCode = '', ...payload }) =>
    api.post('/billing/checkout-session', { ...payload, planId, interval, successUrl, cancelUrl, discountCode }, { timeout: 30000, expectedStatuses: [202] }),
  createLevelTestCheckoutSession: ({ successUrl, cancelUrl } = {}) =>
    api.post('/billing/level-test-checkout-session', { successUrl, cancelUrl }, { timeout: 30000, expectedStatuses: [202] }),
  // Without confirm the server returns the terms to show; with it, checkout.
  upgradeSubscription: ({ planId, interval = 'monthly', confirm = false, successUrl, cancelUrl }) =>
    api.post('/billing/subscription/upgrade', { planId, interval, confirm, successUrl, cancelUrl }, { timeout: 30000, expectedStatuses: [202, 409] }),
  cancelSubscription: ({ confirm = false } = {}) =>
    api.post('/billing/subscription/cancel', { confirm }, { timeout: 30000, expectedStatuses: [409] }),
  // Confirms a Paystack checkout the learner just returned from and grants it.
  verifyPayment: (reference) =>
    api.post('/billing/paystack/verify', { reference }, { timeout: 30000, expectedStatuses: [404] }),
  validateDiscount: ({ planId, discountCode }) =>
    api.post('/billing/discounts/validate', { planId, discountCode }, { expectedStatuses: [404] }),
  openCustomerPortal: (returnUrl) =>
    api.post('/billing/customer-portal', { returnUrl }, { timeout: 30000, expectedStatuses: [202] }),
  sendInstitutionalInquiry: (payload) =>
    api.post('/billing/institutional-inquiry', payload, { timeout: 30000 }),
  getAdminOverview: () =>
    api.get('/billing/admin/overview'),
  getAdminSubscriptions: () =>
    api.get('/billing/admin/subscriptions'),
  getAdminPricing: () =>
    api.get('/billing/admin/pricing'),
  updatePlanOverride: (planId, payload) =>
    api.put(`/billing/admin/plan-overrides/${planId}`, payload),
  createDiscount: (payload) =>
    api.post('/billing/admin/discounts', payload),
  updateDiscount: (discountId, payload) =>
    api.put(`/billing/admin/discounts/${discountId}`, payload),
  assignManualPlan: (payload) =>
    api.post('/billing/admin/manual-plan', payload),
  getAdminOrganizations: () =>
    api.get('/billing/admin/organizations'),
  createOrganization: (payload) =>
    api.post('/billing/admin/organizations', payload),
  updateOrganization: (organizationId, payload) =>
    api.put(`/billing/admin/organizations/${organizationId}`, payload),
  addOrganizationMember: (organizationId, payload) =>
    api.post(`/billing/admin/organizations/${organizationId}/members`, payload),
  assignInstitutionAdmin: (organizationId, payload) =>
    api.post(`/billing/admin/organizations/${organizationId}/institution-admin`, payload),
  removeInstitutionAdmin: (organizationId, membershipId) =>
    api.delete(`/billing/admin/organizations/${organizationId}/institution-admins/${membershipId}`),
  getInstitutionalLeads: (status = '') =>
    api.get('/billing/admin/institutional-leads', { params: status ? { status } : {} }),
  updateInstitutionalLeadStatus: (leadId, status) =>
    api.put(`/billing/admin/institutional-leads/${leadId}/status`, { status }),
  getInstitutionDashboard: (organizationId = '') =>
    api.get('/billing/institution/dashboard', { params: organizationId ? { organizationId } : {}, expectedStatuses: [403] }),
  updateInstitutionProfile: (organizationId, payload) =>
    api.put(`/billing/institution/organizations/${organizationId}`, payload),
  updateInstitutionCertificateBranding: (organizationId, payload) =>
    api.put(`/billing/institution/organizations/${organizationId}/certificate-branding`, payload),
  createInstitutionGroup: (organizationId, payload) =>
    api.post(`/billing/institution/organizations/${organizationId}/groups`, payload),
  updateInstitutionGroup: (organizationId, groupId, payload) =>
    api.put(`/billing/institution/organizations/${organizationId}/groups/${groupId}`, payload),
  addInstitutionMember: (organizationId, payload) =>
    api.post(`/billing/institution/organizations/${organizationId}/members`, payload),
  updateInstitutionMember: (organizationId, membershipId, payload) =>
    api.put(`/billing/institution/organizations/${organizationId}/members/${membershipId}`, payload),
  getSeatWallet: (organizationId) =>
    api.get(`/billing/institution/organizations/${organizationId}/seat-wallet`),
  getSeatProjection: (organizationId, horizonDays = 7) =>
    api.get(`/billing/institution/organizations/${organizationId}/seat-projection`, { params: { horizonDays } }),
  getMemberSeatHistory: (organizationId, membershipId, limit = 50) =>
    api.get(`/billing/institution/organizations/${organizationId}/members/${membershipId}/seat-history`, { params: { limit } }),
  topUpInstitutionSeats: (organizationId, payload) =>
    api.post(`/billing/institution/organizations/${organizationId}/seats`, payload),
  suspendInstitutionMember: (organizationId, membershipId) =>
    api.post(`/billing/institution/organizations/${organizationId}/members/${membershipId}/suspend`),
  unsuspendInstitutionMember: (organizationId, membershipId) =>
    api.post(`/billing/institution/organizations/${organizationId}/members/${membershipId}/unsuspend`, {}, { expectedStatuses: [409] }),
  requestSeatSuspension: (organizationId, membershipId, payload) =>
    api.post(`/billing/institution/organizations/${organizationId}/members/${membershipId}/request-suspension`, payload),
  updateAutoRenew: (organizationId, payload) =>
    api.put(`/billing/institution/organizations/${organizationId}/auto-renew`, payload),
};

// Community posts are durable feed content. Notifications may point to these
// records, but they are not the source of truth for the Community page.
const communityRequestConfig = (config = {}) => ({
  ...config,
  skipLearningProgramHeader: true,
});

// News review calls list their refusal statuses as expected (so a refusal is
// not reported as a crash), which makes axios RESOLVE them. A refusal must
// still reach the caller as an error carrying the server's code and message.
const rejectRefusal = (response) => {
  if (response && response.status >= 400) {
    const error = new Error(response.data?.message || 'Request refused');
    error.response = response;
    error.config = response.config;
    throw error;
  }
  return response;
};

export const communityService = {
  getFeed: ({ page = 1, cursor = '', limit = 20, language = '' } = {}) =>
    api.get('/community/feed', communityRequestConfig({
      params: cursor ? { cursor, limit } : { page, limit },
      appLanguage: language,
    })),
  getPost: (postId, language = '') =>
    api.get(`/community/posts/${postId}`, communityRequestConfig({ expectedStatuses: [404, 410], appLanguage: language })),
  getManagedPosts: (organizationId, page = 1, limit = 20) =>
    api.get('/community/manage/posts', communityRequestConfig({ params: { organizationId, page, limit } })),
  // Returns suggested form values only — nothing is saved until the author
  // reviews the draft and calls createManagedPost/updateManagedPost.
  draftManagedPost: (payload) =>
    api.post('/community/manage/posts/draft', payload, communityRequestConfig({ expectedStatuses: [400, 503, 504] })),
  createManagedPost: (payload) =>
    api.post('/community/manage/posts', payload, communityRequestConfig()),
  updateManagedPost: (postId, payload) =>
    api.patch(`/community/manage/posts/${postId}`, payload, communityRequestConfig()),
  publishManagedPost: (postId) =>
    api.post(`/community/manage/posts/${postId}/publish`, {}, communityRequestConfig()),
  withdrawManagedPost: (postId) =>
    api.delete(`/community/manage/posts/${postId}`, communityRequestConfig()),
  getModerationPosts: ({ status = 'all', page = 1, limit = 20 } = {}) =>
    api.get('/community/moderation/posts', communityRequestConfig({
      params: {
        page,
        limit,
        ...(['hidden', 'visible'].includes(status) ? { moderation: status } : {}),
        ...(['draft', 'published', 'withdrawn'].includes(status) ? { status } : {}),
        publisherType: 'institution',
      },
    })),
  hidePost: (postId, reason) =>
    api.post(`/community/moderation/posts/${postId}/hide`, { reason }, communityRequestConfig()),
  restorePost: (postId, reason = '') =>
    api.post(`/community/moderation/posts/${postId}/restore`, { reason }, communityRequestConfig()),
  getModerationPublishers: ({ status = 'all', page = 1, limit = 50 } = {}) =>
    api.get('/community/moderation/publishers', communityRequestConfig({ params: { status, page, limit } })),
  suspendPublisher: (membershipId, payload) =>
    api.post(`/community/moderation/publishers/${membershipId}/suspend`, payload, communityRequestConfig()),
  restorePublisher: (membershipId, reason = '') =>
    api.post(`/community/moderation/publishers/${membershipId}/restore`, { reason }, communityRequestConfig()),
  getModerationAudit: ({ page = 1, limit = 20 } = {}) =>
    api.get('/community/moderation/audit', communityRequestConfig({ params: { page, limit } })),
  // Weekly news digest review queue (Lingo Booth admins only).
  getNewsQueue: ({ view = 'queue', page = 1, limit = 30 } = {}) =>
    api.get('/community/moderation/news', communityRequestConfig({ params: { view, page, limit } })),
  prepareNews: (week = 'next') =>
    api.post('/community/moderation/news/prepare', { week }, communityRequestConfig({ expectedStatuses: [409, 503] })).then(rejectRefusal),
  approveNews: (postId, publishAt = null) =>
    api.post(`/community/moderation/news/${postId}/approve`, publishAt ? { publishAt } : {}, communityRequestConfig({ expectedStatuses: [409] })).then(rejectRefusal),
  unapproveNews: (postId) =>
    api.post(`/community/moderation/news/${postId}/unapprove`, {}, communityRequestConfig({ expectedStatuses: [409] })).then(rejectRefusal),
  // Moving a post off a day may move later posts up and draft a refill.
  rescheduleNews: (postId, slotAt) =>
    api.post(`/community/moderation/news/${postId}/reschedule`, { slotAt }, communityRequestConfig({ expectedStatuses: [400, 409], timeout: 120000 })).then(rejectRefusal),
  updateNews: (postId, payload) =>
    api.patch(`/community/moderation/news/${postId}`, payload, communityRequestConfig()),
  // Rejecting moves later posts up and may draft a refill, and replacing or
  // composing drafts a new post: these wait on the writing step.
  rejectNews: (postId) =>
    api.post(`/community/moderation/news/${postId}/reject`, {}, communityRequestConfig({ expectedStatuses: [409], timeout: 120000 })).then(rejectRefusal),
  replaceNews: (postId, slotAt = null) =>
    api.post(`/community/moderation/news/${postId}/replace`, slotAt ? { slotAt } : {}, communityRequestConfig({ expectedStatuses: [404, 409, 503], timeout: 120000 })).then(rejectRefusal),
  composeNews: (request, slotAt = null) =>
    api.post('/community/moderation/news/compose', { request, ...(slotAt ? { slotAt } : {}) }, communityRequestConfig({ expectedStatuses: [400, 409, 422, 503], timeout: 120000 })).then(rejectRefusal),
};

export const levelTestService = {
  getContexts: () =>
    api.get('/level-tests/contexts'),
  getOverview: ({ contextType = 'personal', organizationId = '', targetLanguage, nativeLanguage } = {}) => {
    const stored = getLanguageParams();
    return api.get('/level-tests/overview', {
      params: {
        contextType,
        organizationId,
        targetLang: targetLanguage || stored.targetLang,
        nativeLang: nativeLanguage || stored.nativeLang,
      },
    });
  },
  start: ({ level, mode, contextType = 'personal', organizationId = '', targetLanguage, nativeLanguage }) => {
    const stored = getLanguageParams();
    return api.post('/level-tests/start', {
      level,
      mode,
      contextType,
      organizationId,
      targetLanguage: targetLanguage || stored.targetLang,
      nativeLanguage: nativeLanguage || stored.nativeLang,
    }, { expectedStatuses: [403, 404] });
  },
  getAttempt: (attemptId) =>
    api.get(`/level-tests/attempts/${attemptId}`),
  submit: (attemptId, answers) =>
    api.post(`/level-tests/attempts/${attemptId}/submit`, { answers }),
  issueCertificate: (attemptId, payload = {}) =>
    api.post(`/level-tests/attempts/${attemptId}/certificate`, payload, { expectedStatuses: [400, 409] }),
  getInstitutionReport: (organizationId, targetLanguage) => {
    const stored = getLanguageParams();
    return api.get(`/level-tests/institution/${organizationId}/report`, {
      params: { targetLang: targetLanguage || stored.targetLang },
    });
  },
};

// The open-range proficiency exam (backend/routes/proficiency.js). Refusals
// the exam screens handle themselves are expected statuses, so they do not
// surface as generic network errors.
const EXAM_EXPECTED = [402, 404, 409, 410, 429];
export const proficiencyService = {
  getOverview: ({ contextType = 'personal', organizationId = '', targetLanguage, nativeLanguage } = {}) => {
    const stored = getLanguageParams();
    return api.get('/proficiency/overview', {
      params: {
        contextType,
        organizationId,
        targetLang: targetLanguage || stored.targetLang,
        nativeLang: nativeLanguage || stored.nativeLang,
      },
    });
  },
  createExam: ({ contextType = 'personal', organizationId = '', targetLanguage, nativeLanguage } = {}) => {
    const stored = getLanguageParams();
    return api.post('/proficiency/exams', {
      contextType,
      organizationId,
      targetLanguage: targetLanguage || stored.targetLang,
      nativeLanguage: nativeLanguage || stored.nativeLang,
    }, { expectedStatuses: EXAM_EXPECTED });
  },
  getExam: (examId) => api.get(`/proficiency/exams/${examId}`),
  startSection: (examId, section, { sessionId, readiness }) =>
    api.post(`/proficiency/exams/${examId}/sections/${section}/start`, { sessionId, readiness }, { expectedStatuses: EXAM_EXPECTED }),
  resumeSection: (examId, section, { sessionId, afterDisconnect = false }) =>
    api.post(`/proficiency/exams/${examId}/sections/${section}/resume`, { sessionId, afterDisconnect }, { expectedStatuses: EXAM_EXPECTED }),
  getSection: (examId, section, sessionId) =>
    api.get(`/proficiency/exams/${examId}/sections/${section}`, { params: { sessionId }, expectedStatuses: EXAM_EXPECTED }),
  finishSection: (examId, section, sessionId) =>
    api.post(`/proficiency/exams/${examId}/sections/${section}/finish`, { sessionId }, { expectedStatuses: EXAM_EXPECTED }),
  answer: (examId, section, payload) =>
    api.post(`/proficiency/exams/${examId}/sections/${section}/answer`, payload, { expectedStatuses: EXAM_EXPECTED }),
  getListeningAudio: (examId, itemId, sessionId) =>
    api.get(`/proficiency/exams/${examId}/sections/listening/audio/${itemId}`, {
      params: { sessionId }, responseType: 'blob', timeout: 60000,
    }),
  registerPlay: (examId, { sessionId, itemId }) =>
    api.post(`/proficiency/exams/${examId}/sections/listening/play`, { sessionId, itemId }, { expectedStatuses: EXAM_EXPECTED }),
  saveDraft: (examId, payload) =>
    api.post(`/proficiency/exams/${examId}/sections/writing/draft`, payload, { expectedStatuses: EXAM_EXPECTED }),
  submitWriting: (examId, payload) =>
    api.post(`/proficiency/exams/${examId}/sections/writing/submit`, payload, { timeout: 60000, expectedStatuses: EXAM_EXPECTED }),
  uploadSpeakingTurn: (examId, { sessionId, taskId, turnIndex, durationMs, audio, mimeType }) => {
    const form = new FormData();
    if (audio) form.append('audio', audio, `turn-${turnIndex}.${mimeType.includes('mp4') ? 'm4a' : 'webm'}`);
    form.append('mimeType', mimeType || 'audio/webm');
    form.append('sessionId', sessionId);
    form.append('taskId', taskId);
    form.append('turnIndex', String(turnIndex));
    form.append('durationMs', String(Math.round(durationMs || 0)));
    return api.post(`/proficiency/exams/${examId}/sections/speaking/turn`, form, { timeout: 90000, expectedStatuses: EXAM_EXPECTED });
  },
  finishSpeakingTask: (examId, { sessionId, taskId }) =>
    api.post(`/proficiency/exams/${examId}/sections/speaking/finish-task`, { sessionId, taskId }, { timeout: 60000, expectedStatuses: EXAM_EXPECTED }),
  issueCertificate: (examId, payload = {}) =>
    api.post(`/proficiency/exams/${examId}/certificate`, payload, { expectedStatuses: [400, 409] }),
  getInstitutionReport: (organizationId, targetLanguage) => {
    const stored = getLanguageParams();
    return api.get(`/proficiency/institution/${organizationId}/report`, {
      params: { targetLang: targetLanguage || stored.targetLang },
    });
  },
};

export const quizService = {
  getQuizzes: (category, difficulty) => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get('/quiz', { params: { category, difficulty, targetLang, nativeLang } });
  },
  getQuiz: (quizId) => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get(`/quiz/${quizId}`, { params: { targetLang, nativeLang }, timeout: 30000 });
  },
  // Target-language reading-comprehension question for one quiz item. May take a
  // few seconds on first request (LLM generation), then served from cache.
  getComprehension: (quizId, contentIndex) => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get(`/quiz/${quizId}/comprehension`, {
      params: { contentIndex, targetLang, nativeLang },
      timeout: 60000,
    });
  },
};

export const classLessonService = {
  getClassLessons: () => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get('/class-lessons', { params: { targetLang, nativeLang } });
  },
  getClassLessonSummaries: (pair = {}) => {
    const stored = getLanguageParams();
    const targetLang = pair.targetLang || stored.targetLang;
    const nativeLang = pair.nativeLang || stored.nativeLang;
    return cachedGet('/class-lessons', { params: { targetLang, nativeLang, view: 'summary' } }, 60000);
  },
  getClassLesson: (classLessonId) => {
    if (!classLessonId) {
      return Promise.reject(new Error('classLessonId is required'));
    }
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get(`/class-lessons/${classLessonId}`, { params: { targetLang, nativeLang }, timeout: 30000 });
  },
  getClassLessonBootstrap: (classLessonId, { center = 0, windowSize = 8, targetLang: requestedTargetLang, nativeLang: requestedNativeLang } = {}) => {
    if (!classLessonId) {
      return Promise.reject(new Error('classLessonId is required'));
    }
    const stored = getLanguageParams();
    const targetLang = requestedTargetLang || stored.targetLang;
    const nativeLang = requestedNativeLang || stored.nativeLang;
    return cachedGet(`/class-lessons/${classLessonId}/bootstrap`, {
      params: { targetLang, nativeLang, center, windowSize },
      timeout: 30000,
    }, 5000);
  },
  getClassLessonItems: (classLessonId, { center = 0, windowSize = 8 } = {}) => {
    if (!classLessonId) {
      return Promise.reject(new Error('classLessonId is required'));
    }
    const { targetLang, nativeLang } = getLanguageParams();
    return cachedGet(`/class-lessons/${classLessonId}/items`, {
      params: { targetLang, nativeLang, center, windowSize },
      timeout: 30000,
    }, 30000);
  },
  preparePair: ({ targetLang, nativeLang } = {}) => {
    const stored = getLanguageParams();
    return api.post('/class-lessons/prepare-pair', {
      targetLang: targetLang || stored.targetLang,
      nativeLang: nativeLang || stored.nativeLang,
    }, { expectedStatuses: [202] });
  },
  getProgress: (classLessonId) => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get(`/class-lessons/${classLessonId}/progress`, { params: { targetLang, nativeLang } });
  },
  saveProgress: (classLessonId, progress) => {
    const { targetLang: targetLanguage, nativeLang: nativeLanguage } = getLanguageParams();
    return api.put(`/class-lessons/${classLessonId}/progress`, {
      ...progress,
      targetLanguage,
      nativeLanguage,
      source: 'web',
    }).then((response) => {
      invalidateCachedGets((key) => (
        key.includes(`/class-lessons/${classLessonId}/bootstrap`)
        || key.includes('/learning-hub/overview')
      ));
      return response;
    });
  },
};

export const learningHubService = {
  getPrograms: () =>
    cachedGet('/learning-hub/programs', { expectedStatuses: [401, 404] }, 10000),
  switchProgram: (programId) =>
    api.put('/learning-hub/programs/active', { programId }).then((response) => {
      invalidateCachedGets(() => true);
      return response;
    }),
  getContexts: () =>
    api.get('/learning-hub/contexts', { expectedStatuses: [401] }),
  getOverview: () => {
    const { targetLang, nativeLang } = getLanguageParams();
    return cachedGet('/learning-hub/overview', { params: { targetLang, nativeLang } }, 10000);
  },
  getSavedItems: () => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get('/learning-hub/saved-items', { params: { targetLang, nativeLang } });
  },
  saveItem: (payload) => {
    const { targetLang: targetLanguage, nativeLang: nativeLanguage } = getLanguageParams();
    return api.post('/learning-hub/saved-items', {
      ...payload,
      targetLanguage,
      nativeLanguage,
    }).then((response) => {
      invalidateCachedGets((key) => key.includes('/learning-hub/overview'));
      return response;
    });
  },
  reviewItem: (itemId, result) =>
    api.put(`/learning-hub/saved-items/${itemId}/review`, { result }).then((response) => {
      invalidateCachedGets((key) => key.includes('/learning-hub/overview'));
      return response;
    }),
  deleteItem: (itemId) =>
    api.delete(`/learning-hub/saved-items/${itemId}`).then((response) => {
      invalidateCachedGets((key) => key.includes('/learning-hub/overview'));
      return response;
    }),
  search: (query) => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get('/learning-hub/search', { params: { targetLang, nativeLang, q: query } });
  },
  getPairProfile: () => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get('/learning-hub/pair-profile', { params: { targetLang, nativeLang } });
  },
  startPlacementCheck: () => {
    const { targetLang: targetLanguage, nativeLang: nativeLanguage } = getLanguageParams();
    return api.post('/learning-hub/placement-checks/start', { targetLanguage, nativeLanguage });
  },
  // CEFR (A1-C2) placement: meter + get the first stage of the adaptive test,
  // or {kind:'fallback'}. Each finished stage goes to answerPlacement, which
  // returns the next stage or the result.
  startPlacement: () => {
    const { targetLang: targetLanguage, nativeLang: nativeLanguage } = getLanguageParams();
    return api.post('/learning-hub/placement/start', { targetLanguage, nativeLanguage, adaptive: true }, { expectedStatuses: [429] });
  },
  answerPlacement: (attemptId, answers) =>
    api.post(`/learning-hub/placement/${encodeURIComponent(attemptId)}/answers`, { answers }),
  savePairProfile: (payload) => {
    const { targetLang: targetLanguage, nativeLang: nativeLanguage } = getLanguageParams();
    return api.put('/learning-hub/pair-profile', {
      ...payload,
      targetLanguage,
      nativeLanguage,
    }).then((response) => {
      invalidateCachedGets((key) => key.includes('/learning-hub/overview'));
      return response;
    });
  },
};

export const practiceContextService = {
  analyze: ({ transcript, nativeLanguage, targetLanguage, source = 'web' }) =>
    api.post('/practice-context/analyze', { transcript, nativeLanguage, targetLanguage, source }, { timeout: 30000 }),
  save: (context) =>
    api.post('/practice-context', { ...context, source: context.source || 'web' }),
  list: (targetLanguage) =>
    api.get('/practice-context', { params: { targetLanguage } }),
  recommendations: (targetLanguage) =>
    api.get('/practice-context/recommendations', {
      params: { targetLanguage },
      // Recommendations are optional UI sugar — entitlement (403) or missing
      // backend route (404) shouldn't show up as admin-side failures.
      expectedStatuses: [403, 404],
    }),
  delete: (contextId) =>
    api.delete(`/practice-context/${contextId}`),
};

export const flashcardService = {
  getCategories: () => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get('/flashcards/categories', { params: { targetLang, nativeLang } });
  },
  getCategoryCards: (category) => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get('/flashcards/category-cards', { params: { targetLang, nativeLang, category } });
  },
  getFlashcards: (userId, page = 1, limit = 50, opts = {}) => {
    const { targetLang, nativeLang } = getLanguageParams();
    const params = { targetLang, nativeLang, page, limit };
    if (opts.categories) params.categories = opts.categories;
    if (opts.shuffle !== undefined) params.shuffle = opts.shuffle;
    if (opts.seed !== undefined) params.seed = opts.seed;
    if (opts.scope && opts.scope !== 'all') params.scope = opts.scope;
    return api.get(`/flashcards/user/${userId}`, { params });
  },
  getGuestFlashcards: (page = 1, limit = 50, opts = {}) => {
    const { targetLang, nativeLang } = getLanguageParams();
    const params = { nativeLang, targetLang, page, limit };
    if (opts.categories) params.categories = opts.categories;
    if (opts.shuffle !== undefined) params.shuffle = opts.shuffle;
    if (opts.seed !== undefined) params.seed = opts.seed;
    return api.get('/flashcards/guest', { params });
  },
  createFlashcard: (flashcardData) => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.post('/flashcards', { targetLang, nativeLang, ...flashcardData });
  },
  updateFlashcard: (id, data) =>
    api.put(`/flashcards/${id}`, data),
  setCardFocus: (id, value) => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.put(`/flashcards/${id}/focus`, { value, targetLang, nativeLang });
  },
  getFocusIds: () => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get('/flashcards/focus-ids', { params: { targetLang, nativeLang } });
  },
  deleteFlashcard: (id) =>
    api.delete(`/flashcards/${id}`),
};

export const readingService = {
  // Adaptive tier suggestion + how many known words the learner has.
  recommend: () => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.get('/reading/recommend', { params: { targetLang, nativeLang } });
  },
  // Generate a reading. `complexity` is optional — omit to accept the adaptive
  // recommendation. Generation runs an LLM round-trip, so allow a long timeout.
  generate: (complexity) => {
    const { targetLang, nativeLang } = getLanguageParams();
    // 429 (daily quota) and 503 (model unavailable) are product answers, not
    // defects: they resolve, and the page reads `status` to pick its message.
    return api.post('/reading/generate', { targetLang, nativeLang, complexity }, { timeout: 45000, expectedStatuses: [429, 503] });
  },
  // Submit answers for grading (open answers are graded server-side).
  submit: (readingId, answers) =>
    api.post(`/reading/${readingId}/submit`, { answers }, { timeout: 45000 }),
};

export const progressService = {
  getProgress: (userId) =>
    api.get(`/progress/user/${userId}`, { params: getLanguageParams() }),
  getSummary: (userId) =>
    api.get(`/progress/summary/${userId}`, { params: getLanguageParams() }),
  recordProgress: (progressData) => {
    const { targetLang: targetLanguage, nativeLang: nativeLanguage } = getLanguageParams();
    return api.post('/progress', {
      ...progressData,
      targetLanguage,
      nativeLanguage,
    }).then((response) => {
      invalidateCachedGets((key) => key.includes('/learning-hub/overview'));
      return response;
    });
  },
};

export const userService = {
  getProfile: (userId) =>
    cachedGet(`/users/${userId}`, {}, 10000),
  updateProfile: (userId, data) =>
    api.put(`/users/${userId}`, data).then((response) => {
      invalidateCachedGets((key) => key.includes(`/users/${userId}`));
      return response;
    }),
  updateCurriculumPreference: (userId, payload) =>
    api.put(`/users/${userId}/curriculum-preference`, payload).then((response) => {
      invalidateCachedGets((key) => key.includes(`/users/${userId}`));
      return response;
    }),
  changePassword: (userId, data) =>
    api.put(`/users/${userId}/password`, data),
  deleteAccount: (userId) =>
    api.delete(`/users/${userId}`),
  saveActivityState: (userId, data) =>
    api.put(`/users/${userId}/activity-state`, data).then((response) => {
      invalidateCachedGets((key) => key.includes(`/users/${userId}/activity-state`));
      return response;
    }),
  getActivityState: (userId) =>
    cachedGet(`/users/${userId}/activity-state`, {}, 5000),
  // Seed + saved prefs in one call. Both live on the same User document, and the
  // flashcard screen needs both before it can request a deck, so fetching them
  // separately put two serial round-trips in front of the first card.
  getFlashcardSession: (userId) =>
    api.get(`/users/${userId}/flashcard-session`),
  // Resolve the current 12h-stable flashcard shuffle seed (server-side window).
  getFlashcardSeed: (userId) =>
    api.get(`/users/${userId}/flashcard-seed`),
  // Force a fresh seed and restart the 12h window (manual reshuffle).
  refreshFlashcardSeed: (userId) =>
    api.post(`/users/${userId}/flashcard-seed`),
  // Persisted flashcard deck selection + study settings (server-side, follows
  // the learner across devices). Guests fall back to localStorage client-side.
  getFlashcardPrefs: (userId) =>
    api.get(`/users/${userId}/flashcard-prefs`),
  saveFlashcardPrefs: (userId, prefs) =>
    api.put(`/users/${userId}/flashcard-prefs`, { prefs }),
  addXP: (userId, points) =>
    api.post(`/users/${userId}/xp`, { points }).then((response) => {
      invalidateCachedGets((key) => (
        key.includes(`/users/${userId}/xp-stats`)
      ));
      return response;
    }),
  awardXP: (userId, data) => {
    const { targetLang: targetLanguage, nativeLang: nativeLanguage } = getLanguageParams();
    return api.post(`/users/${userId}/award-xp`, {
      ...data,
      targetLanguage,
      nativeLanguage,
    }).then(res => {
      invalidateCachedGets((key) => (
        key.includes(`/users/${userId}/xp-stats`)
      ));
      window.dispatchEvent(new CustomEvent('xpUpdated', { detail: res.data }));
      return res;
    });
  },
  recordLearningEvent: (userId, data) => {
    const { targetLang, nativeLang } = getLanguageParams();
    return api.post(`/users/${userId}/learning-events`, {
      ...data,
      targetLanguage: targetLang,
      nativeLanguage: nativeLang,
    }).then((res) => {
      invalidateCachedGets((key) => (
        key.includes(`/users/${userId}/xp-stats`)
        || key.includes('/learning-hub/overview')
      ));
      window.dispatchEvent(new CustomEvent('xpUpdated', { detail: res.data }));
      return res;
    });
  },
  recordPeek: (userId, data) =>
    api.post(`/users/${userId}/peek`, data),
  resetXP: (userId) =>
    api.post(`/users/${userId}/reset-xp`).then((response) => {
      invalidateCachedGets((key) => key.includes(`/users/${userId}/xp-stats`));
      return response;
    }),
  getXpStats: (userId) =>
    cachedGet(`/users/${userId}/xp-stats`, {}, 10000),
};

export const ttsService = {
  speak: (text, lang, voice, rate) =>
    api.post('/tts', { text, lang, voice, rate }, { responseType: 'blob' }),
  getVoices: (lang) =>
    api.get('/tts/voices', { params: lang ? { lang } : {} }),
  // Builds a direct GET URL so the frontend can set audio.src without a fetch,
  // keeping audio.play() synchronous within the user gesture on iOS Safari.
  //
  // Returns '' when there is nothing for a voice to pronounce — every caller
  // must skip an empty URL. This is the chokepoint every TTS request in the web
  // app passes through, so no code path can ask the backend to synthesize a
  // lone "," or "→" (which is what produced 340 TTS failures in two days).
  buildSpeakUrl: (text, lang, voice, rate) => {
    if (!isSpeakable(text)) return '';
    const params = new URLSearchParams({ text: String(text || '').trim(), lang });
    if (voice) params.set('voice', voice);
    if (rate) params.set('rate', rate);
    return `${API_URL}/tts?${params.toString()}`;
  },
};

const adminTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export const adminService = {
  // tz makes "Active today" the admin's own calendar day.
  getStats: () =>
    api.get('/admin/stats', { params: { tz: adminTimeZone() } }),
  // Every account active in a window, paginated. Same "active" definition as
  // the headline counts on /stats.
  getActiveAccounts: ({ range = 'today', from = '', to = '', page = 1, limit = 50, search = '', feature = '', sort = 'recent' } = {}) =>
    api.get('/admin/active-accounts', {
      params: { range, from, to, page, limit, search, feature, sort, tz: adminTimeZone() },
    }),
  exportActiveAccounts: ({ range = 'today', from = '', to = '', search = '', feature = '', sort = 'recent' } = {}) =>
    api.get('/admin/active-accounts', {
      params: { range, from, to, search, feature, sort, tz: adminTimeZone(), format: 'csv' },
      responseType: 'blob',
      timeout: 60000,
    }),
  // Whether the current admin is the account owner (only the owner can manage
  // admins). Drives which role controls the UI shows; backend still enforces.
  whoami: () =>
    api.get('/admin/whoami'),
  // Time-series audit feed of admin actions (newest first).
  getActivity: ({ page = 1, action = '', actorEmail = '', success = '' } = {}) =>
    api.get('/admin/activity', { params: { page, action, actorEmail, success } }),
  getCommunicationHistory: ({ page = 1, channel = 'all' } = {}) =>
    api.get('/admin/communication-history', { params: { page, channel } }),
  getUsers: () =>
    api.get('/admin/users'),
  getUser: (userId) =>
    api.get(`/admin/users/${userId}`),
  suspendUser: (userId, reason) =>
    api.put(`/admin/users/${userId}/suspend`, { reason }),
  unsuspendUser: (userId) =>
    api.put(`/admin/users/${userId}/unsuspend`),
  updateUserRole: (userId, role) =>
    api.put(`/admin/users/${userId}/role`, { role }),
  deleteUser: (userId) =>
    api.delete(`/admin/users/${userId}`),
  getUserFlashcards: () =>
    api.get('/admin/flashcards'),
  deleteFlashcard: (flashcardId) =>
    api.delete(`/admin/flashcards/${flashcardId}`),
  getGuests: (page = 1) =>
    api.get(`/admin/guests?page=${page}`),
  getErrorReports: ({ page = 1, status = 'open', severity = '', source = '' } = {}) =>
    api.get('/admin/error-reports', { params: { page, status, severity, source } }),
  acknowledgeErrorReport: (reportId) =>
    api.put(`/admin/error-reports/${reportId}/acknowledge`),
  clearOpenErrorReports: () =>
    api.put('/admin/error-reports/clear-open'),
  getPlatformHealth: ({ hours = 24 } = {}) =>
    api.get('/admin/platform-health', { params: { hours } }),
  getOwnLanguageCatalog: () =>
    api.get('/admin/own-language/catalog'),
  getOwnLanguageLesson: (lang, lessonId) =>
    api.get(`/admin/own-language/${encodeURIComponent(lang)}/lessons/${encodeURIComponent(lessonId)}`),
  getContactMessages: ({ page = 1, status = 'open', senderType = 'all' } = {}) =>
    api.get('/admin/contact-messages', { params: { page, status, senderType } }),
  acknowledgeContactMessage: (messageId) =>
    api.put(`/admin/contact-messages/${messageId}/acknowledge`),
  replyToContactMessage: (messageId, payload) =>
    api.post(`/admin/contact-messages/${messageId}/reply`, payload, { timeout: 120000 }),
  draftContactReply: (messageId, guidance = '') =>
    api.post(`/admin/contact-messages/${messageId}/draft-reply`, { guidance }, { timeout: 120000 }),
  clearOpenContactMessages: () =>
    api.put('/admin/contact-messages/clear-open'),
  getSemesterInterest: ({ page = 1, status = 'all' } = {}) =>
    api.get('/admin/semester-interest', { params: { page, status } }),
  updateSemesterInterestStatus: (leadId, status) =>
    api.put(`/admin/semester-interest/${leadId}/status`, { status }),
  // Cohort management (web-only admin).
  getCohorts: () => api.get('/admin/cohorts'),
  createCohort: (payload) => api.post('/admin/cohorts', payload),
  updateCohort: (cohortId, payload) => api.put(`/admin/cohorts/${cohortId}`, payload),
  deleteCohort: (cohortId) => api.delete(`/admin/cohorts/${cohortId}`),
  getCohortEnrollments: (cohortId) => api.get(`/admin/cohorts/${cohortId}/enrollments`),
  updateCohortEnrollment: (cohortId, enrollmentId, status) =>
    api.put(`/admin/cohorts/${cohortId}/enrollments/${enrollmentId}`, { status }),
  reassignCohortEnrollment: (cohortId, enrollmentId, targetCohortId) =>
    api.put(`/admin/cohorts/${cohortId}/enrollments/${enrollmentId}/reassign`, { targetCohortId }),
  // Cohort messages: the three pre-start emails plus any custom notices.
  getCohortMessages: (cohortId) => api.get(`/admin/cohorts/${cohortId}/messages`),
  // The cohort-level switches for automatic mail. Separate from the per-stage
  // flags: this is the one control that silences every automatic email for a
  // cohort at once.
  updateCohortMailPolicy: (cohortId, payload) =>
    api.patch(`/admin/cohorts/${cohortId}/messages/policy`, payload),
  createCohortMessage: (cohortId, payload) => api.post(`/admin/cohorts/${cohortId}/messages`, payload),
  updateCohortMessage: (cohortId, messageId, payload) =>
    api.put(`/admin/cohorts/${cohortId}/messages/${messageId}`, payload),
  deleteCohortMessage: (cohortId, messageId) =>
    api.delete(`/admin/cohorts/${cohortId}/messages/${messageId}`, { expectedStatuses: [409] }),
  previewCohortMessage: (cohortId, messageId, payload = {}) =>
    api.post(`/admin/cohorts/${cohortId}/messages/${messageId}/preview`, payload),
  testCohortMessage: (cohortId, messageId, payload = {}) =>
    api.post(`/admin/cohorts/${cohortId}/messages/${messageId}/test`, payload),
  sendCohortMessage: (cohortId, messageId, options = {}) =>
    api.post(`/admin/cohorts/${cohortId}/messages/${messageId}/send`, options, { expectedStatuses: [502] }),
  getCohortClasses: (cohortId) => api.get(`/admin/cohorts/${cohortId}/classes`),
  generateCohortClasses: (cohortId, regenerate = false) => api.post(`/admin/cohorts/${cohortId}/classes/generate`, { regenerate }),
  updateCohortClass: (cohortId, classId, payload) => api.put(`/admin/cohorts/${cohortId}/classes/${classId}`, payload),
  moveCohortClassMember: (cohortId, classId, enrollmentId) => api.put(`/admin/cohorts/${cohortId}/classes/${classId}/members/${enrollmentId}`),
  getCohortScholarships: (cohortId) => api.get(`/admin/cohorts/${cohortId}/scholarships`),
  getCohortScholarshipEvidence: (cohortId, applicationId, evidenceId) =>
    api.get(`/admin/cohorts/${cohortId}/scholarships/${applicationId}/evidence/${evidenceId}`, { responseType: 'blob' }),
  decideCohortScholarship: (cohortId, applicationId, payload) =>
    api.put(`/admin/cohorts/${cohortId}/scholarships/${applicationId}`, payload),
  getSemesterPrograms: () => api.get('/admin/semester-programs'),
  createSemesterProgram: (payload) => api.post('/admin/semester-programs', payload),
  updateSemesterProgram: (programId, payload) => api.put(`/admin/semester-programs/${programId}`, payload),
  getReviews: ({ page = 1, status = 'pending' } = {}) =>
    api.get('/admin/reviews', { params: { page, status } }),
  updateReviewStatus: (reviewId, status) =>
    api.put(`/admin/reviews/${reviewId}/status`, { status }),
  deleteReview: (reviewId) =>
    api.delete(`/admin/reviews/${reviewId}`),
  sendEmail: (payload) =>
    api.post('/admin/send-email', payload, { timeout: 120000 }),
  draftAdminMessage: (payload) =>
    api.post('/admin/communications/draft', payload, { timeout: 120000 }),
  sendSpeakingDemoTurn: (data) =>
    api.post('/admin/speaking-demo/conversation', data, { timeout: 60000 }),
  sendLocalSpeakingDemoTurn: (data) =>
    api.post('/admin/local-demo/speaking-demo/conversation', data, { timeout: 60000 }),
};

export const referralService = {
  // Public: fire-and-forget visit + signup attribution.
  recordHit: (code) =>
    api.post('/referrals/hit', { code }),
  attributeSignup: (code) =>
    api.post('/referrals/attribute-signup', { code }),
  // Admin / marketing: manage links.
  list: () =>
    api.get('/referrals'),
  create: (payload) =>
    api.post('/referrals', payload),
  update: (id, payload) =>
    api.patch(`/referrals/${id}`, payload),
  remove: (id) =>
    api.delete(`/referrals/${id}`),
  getStats: (id, days = 30) =>
    api.get(`/referrals/${id}/stats`, { params: { days } }),
  // Admin / marketing: aggregate visitor analytics (funnel, geo, device,
  // engagement, time-per-page/activity) over the last N days.
  getAnalytics: (days = 30) =>
    api.get('/referrals/analytics', { params: { days } }),
  // Admin only: grant/revoke the marketing role by email.
  assignRole: (email, role) =>
    api.patch('/admin/users/role', { email, role }),
  // Admin only: list the accounts that currently hold a privileged role.
  listAdmins: () =>
    api.get('/admin/users', { params: { role: 'admin,marketing' } }),
};

export const aiService = {
  getEntitlements: () =>
    cachedGet('/ai/entitlements', {}, 10000),
  sendConversationTurn: (data) =>
    api.post('/ai/conversation', data, { timeout: 60000 }),
};

function asrAudioExtension(mimeType) {
  const type = String(mimeType || '').toLowerCase();
  if (type.includes('mp4') || type.includes('m4a') || type.includes('aac')) return 'mp4';
  if (type.includes('ogg')) return 'ogg';
  if (type.includes('wav')) return 'wav';
  if (type.includes('mpeg') || type.includes('mp3')) return 'mp3';
  return 'webm';
}

export function buildAsrFormData({ audio, mimeType = audio?.type || 'audio/webm', language = '', durationMs, purpose = '' }) {
  if (!audio) throw new TypeError('ASR audio is required.');
  const form = new FormData();
  const safeMimeType = String(mimeType || audio.type || 'audio/webm');
  form.append('audio', audio, `speech.${asrAudioExtension(safeMimeType)}`);
  form.append('mimeType', safeMimeType);
  const canonicalLanguage = normalizeLanguageCode(language);
  if (canonicalLanguage) form.append('language', canonicalLanguage);
  if (Number.isFinite(Number(durationMs)) && Number(durationMs) >= 0) {
    form.append('durationMs', String(Math.round(Number(durationMs))));
  }
  if (purpose) form.append('purpose', String(purpose).slice(0, 80));
  return form;
}

export const curriculumV2Service = {
  listLessons: () => api.get('/curriculum/v2/lessons'),
  getLesson: (id, params = {}) => api.get(`/curriculum/v2/lessons/${encodeURIComponent(id)}`, { params }),
  getPlan: (params = {}) => api.get('/curriculum/v2/plan', { params }),
  // 409 is the cohort-program guard, handled by the caller rather than reported:
  // semester work is only recorded while the cohort is the current program.
  markComplete: (id, semester = {}) => api.post(`/curriculum/v2/lessons/${encodeURIComponent(id)}/complete`, {
    cohortId: semester.cohortId || undefined,
    semesterActivityId: semester.semesterActivityId || undefined,
  }, { expectedStatuses: [409] }),
  // Stepping away from a lesson the learner could not finish. Not a
  // completion: it schedules an easier rung on the same concept and leaves
  // progress (and therefore prerequisite unlocking) untouched.
  deferLesson: ({ lessonId, targetLang, sessionId }) =>
    api.post(`/curriculum/v2/lessons/${encodeURIComponent(lessonId)}/defer`, {
      targetLang: normalizeLanguageCode(targetLang) || undefined,
      sessionId: sessionId || undefined,
    }),
  getProgress: () => api.get('/curriculum/v2/progress'),
  evaluateProduction: ({ lessonId, drillIndex, fillerConceptId, learnerText }) =>
    api.post('/curriculum/v2/feedback',
      { lessonId, drillIndex, fillerConceptId, learnerText },
      { timeout: 30000 }),
  evaluatePronunciation: ({ target, transcript, targetLang }) =>
    api.post('/curriculum/v2/pronunciation-check',
      { target, transcript, targetLang: normalizeLanguageCode(targetLang) || undefined },
      { timeout: 30000 }),
  recordSrsReview: ({ conceptId, conceptKind, skill, outcome, targetLang }) =>
    api.post('/curriculum/v2/srs/review',
      { conceptId, conceptKind, skill, outcome, targetLang },
      { timeout: 10000 }),
  recordEvent: ({ conceptId, lessonId, lessonType, outcome, hintUsed, latencyMs, sessionId, targetLang, contextSignal }) =>
    api.post('/curriculum/v2/events',
      { conceptId, lessonId, lessonType, outcome, hintUsed, latencyMs, sessionId, targetLang, contextSignal },
      { timeout: 5000, background: true }),
  // Batched native-language reveal counters for the per-band disclosure taper.
  // One request per session flush, not one per tap.
  recordReveals: ({ targetLang, sessionId, buckets }) =>
    api.post('/curriculum/v2/disclosure/reveals',
      { targetLang, sessionId, buckets },
      { timeout: 5000 }),
  // Alphabet / phonics onboarding — target-aware. Defaults to the learner's
  // current target language: Korean (ko) is served the Hangul module, English
  // (en) the phonics module, via the generic /alphabet/* endpoints. The
  // backend maps ko back to the legacy hangulProgress store, so Korean
  // behaviour is unchanged.
  getAlphabetGroups: (targetLang = getStoredLanguageCode('targetLanguage') || 'ko') =>
    api.get('/curriculum/v2/alphabet/groups', { params: { targetLang } }),
  getAlphabetProgress: (targetLang = getStoredLanguageCode('targetLanguage') || 'ko') =>
    api.get('/curriculum/v2/alphabet/progress', { params: { targetLang } }),
  completeAlphabetGroup: (groupId, targetLang = getStoredLanguageCode('targetLanguage') || 'ko') =>
    api.post(`/curriculum/v2/alphabet/groups/${encodeURIComponent(groupId)}/complete`, { targetLang }),
  skipAlphabet: (targetLang = getStoredLanguageCode('targetLanguage') || 'ko') =>
    api.post('/curriculum/v2/alphabet/skip', { targetLang }),
  // Catalog (Phase 3)
  getCatalog: (params = {}) => api.get('/curriculum/v2/catalog', { params }),
  // Per-CEFR-level completion status (drives the Learn-home completion cards).
  getConceptLessons: (conceptId, params = {}) =>
    api.get(`/curriculum/v2/concepts/${encodeURIComponent(conceptId)}/lessons`, { params, expectedStatuses: [404] }),
  // Server-side ASR (Whisper)
  getAsrStatus: () => api.get('/curriculum/v2/asr/status'),
  transcribeAudio: ({ audio, mimeType = audio?.type || 'audio/webm', language = '', durationMs, purpose = '', signal }) =>
    api.post('/curriculum/v2/asr/transcribe',
      buildAsrFormData({ audio, mimeType, language, durationMs, purpose }),
      { timeout: 35000, expectedStatuses: [400, 413, 429, 502, 503], signal }),
  // Second pass after a low pronunciation score: what language was the clip
  // actually in? Pins nothing, so the answer is the provider's own judgement.
  detectAudioLanguage: ({ audio, mimeType = audio?.type || 'audio/webm', durationMs, signal }) =>
    api.post('/curriculum/v2/asr/detect-language',
      buildAsrFormData({ audio, mimeType, durationMs, purpose: 'language-detection' }),
      { timeout: 35000, expectedStatuses: [400, 413, 429, 502, 503], signal }),
};

export default api;
